import { Check, Download, Eye, Paperclip, X } from 'lucide-react';

import type { AccountContact } from '@/api/commercial/accounts';
import type { CreditAnalysis } from '@/api/commercial/credit-analysis';
import { DocumentApprovalDialog } from '@/components/dialogs/commercial/credit-analysis/document-approval-dialog';
import { DocumentRejectionDialog } from '@/components/dialogs/commercial/credit-analysis/document-rejection-dialog';
import { ResendDocumentRequestDialog } from '@/components/forms/commercial/credit-analysis/resend-document-request-dialog';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import type { AccountType } from '@/hooks/commercial/use-credit-analysis-state-machine';
import { useDocumentUploadTabAnalysis } from './use-document-upload-tab-analysis';

interface DocumentsUploadFormTabAnalysisProps {
  creditAnalysis: CreditAnalysis | null;
  proposalId: string;
  account: { id: string; accountType?: string } | null;
  accountType: AccountType;
  contacts?: AccountContact[];
  recipients?: string[];
}

const getFileExtension = (fileName: string) => {
  const parts = fileName.split('.');
  return parts.length > 1 ? parts.pop() : '';
};

export function DocumentsUploadFormTabAnalysis({
  creditAnalysis,
  proposalId,
  account,
  accountType,
  contacts = [],
  recipients = [],
}: DocumentsUploadFormTabAnalysisProps) {
  const hook = useDocumentUploadTabAnalysis({
    creditAnalysis,
    proposalId,
    account,
    accountType,
    contacts,
  });

  return (
    <>
      <div className="space-y-6">
        <div className="space-y-3">
          {hook.currentFields.map((field) => {
            const isUploaded = hook.documentFilesMap.has(field.key);
            const uploadedFileMetadata = hook.documentFilesMap.get(field.key);
            const review = hook.documentReviewsMap.get(field.key);
            const file = hook.files[field.key];
            const progress = hook.uploadProgress[field.key];

            return (
              <div key={field.key} className="space-y-1">
                <div className="flex items-center justify-between gap-4 px-4 py-3 border border-input rounded-md bg-background">
                  <div className="flex-1">
                    <Label htmlFor={field.key} className="flex items-center gap-2">
                      {field.label}
                      {field.required && <span className="text-destructive">*</span>}
                    </Label>
                  </div>

                  {/* Document uploaded - show View, Download, and Review buttons */}
                  {isUploaded && uploadedFileMetadata && review?.status !== 'rejected' && (
                    <div className="flex gap-2 items-center">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        type="button"
                        onClick={() => hook.handleViewDocument(field.key)}
                      >
                        <Eye className="h-4 w-4" />
                        Visualizar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        type="button"
                        onClick={() => hook.handleDownloadDocument(field.key)}
                      >
                        <Download className="h-4 w-4" />
                        Baixar
                      </Button>
                      {(!review || review.status === 'pending') && hook.canReviewDocuments && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                            type="button"
                            onClick={() => hook.handleOpenApprovalDialog(field.key, field.label)}
                          >
                            <Check className="h-4 w-4" />
                            Aprovar
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                            type="button"
                            onClick={() => hook.handleOpenRejectionDialog(field.key, field.label)}
                          >
                            <X className="h-4 w-4" />
                            Reprovar
                          </Button>
                        </>
                      )}
                      {review?.status === 'approved' && (
                        <span className="inline-flex h-fit items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-green-700 bg-green-100">
                          <Check className="h-3 w-3" />
                          Documento aprovado
                        </span>
                      )}
                    </div>
                  )}

                  {/* Document rejected - show View, Download of old file + Attach button for re-upload */}
                  {isUploaded && uploadedFileMetadata && review?.status === 'rejected' && (
                    <div className="flex gap-2 items-center">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        type="button"
                        onClick={() => hook.handleViewDocument(field.key)}
                      >
                        <Eye className="h-4 w-4" />
                        Visualizar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        type="button"
                        onClick={() => hook.handleDownloadDocument(field.key)}
                      >
                        <Download className="h-4 w-4" />
                        Baixar
                      </Button>
                      <input
                        type="file"
                        id={`rejected-${field.key}`}
                        className="hidden"
                        accept={field.accept}
                        onChange={(e) => {
                          const selectedFile = e.target.files?.[0] || null;
                          hook.handleFileChange(field.key, selectedFile);
                        }}
                        disabled={hook.isUploading || !(hook as any).canUploadDocuments}
                      />
                      {!file ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-2 shrink-0"
                          type="button"
                          onClick={() => document.getElementById(`rejected-${field.key}`)?.click()}
                          disabled={hook.isUploading || !(hook as any).canUploadDocuments}
                        >
                          <Paperclip className="h-4 w-4" />
                          Reenviar
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          type="button"
                          onClick={() => hook.handleFileChange(field.key, null)}
                          disabled={hook.isUploading || !(hook as any).canDeleteDocuments}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          Remover
                        </Button>
                      )}
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-red-700 bg-red-100">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-600" />
                        Documento reprovado
                      </span>
                    </div>
                  )}

                  {/* No document uploaded - show attach button */}
                  {!isUploaded && (
                    <div className="flex gap-2 items-center">
                      {!file && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-gray-700 bg-gray-100">
                          <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                          Documento não enviado
                        </span>
                      )}
                      <input
                        type="file"
                        id={field.key}
                        className="hidden"
                        accept={field.accept}
                        onChange={(e) => {
                          const selectedFile = e.target.files?.[0] || null;
                          hook.handleFileChange(field.key, selectedFile);
                        }}
                        disabled={hook.isUploading || !(hook as any).canUploadDocuments}
                      />
                      {!file ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-2 shrink-0"
                          type="button"
                          onClick={() => document.getElementById(field.key)?.click()}
                          disabled={hook.isUploading || !(hook as any).canUploadDocuments}
                        >
                          <Paperclip className="h-4 w-4" />
                          Anexar
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          type="button"
                          onClick={() => hook.handleFileChange(field.key, null)}
                          disabled={hook.isUploading || !(hook as any).canDeleteDocuments}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          Remover
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {/* Upload Progress */}
                {hook.isUploading && progress !== undefined && progress > 0 && progress < 100 && (
                  <div className="mt-2">
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className="bg-primary h-2 rounded-full transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Selected file display */}
                {file && !isUploaded && (
                  <div className="mt-2 flex items-center gap-3 rounded-lg border border-sky-100 bg-sky-50 px-4 py-3 shadow-sm">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md bg-sky-100 text-sky-700">
                      <Paperclip className="h-5 w-5" />
                    </div>
                    <div className="flex flex-col flex-1">
                      <span className="font-medium text-foreground">{field.label}</span>
                      <span className="text-sm text-muted-foreground">
                        {file.name}
                        {getFileExtension(file.name) ? ` • ${getFileExtension(file.name)}` : ''}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Correction request button - shows when IN_ANALYSIS and has rejected docs */}
        {creditAnalysis?.status === 'IN_ANALYSIS' &&
          hook.currentFields.some((field) => {
            const review = hook.documentReviewsMap.get(field.key);
            return review?.status === 'rejected';
          }) && (
            <div className="flex justify-end">
              <Button variant="outline" type="button" onClick={() => hook.handleOpenResendDialog()}>
                Solicitar correção
              </Button>
            </div>
          )}
      </div>

      {/* Document Preview Dialog */}
      <Dialog
        open={!!hook.previewFile}
        onOpenChange={(open) => !open && hook.handleClosePreviewDialog()}
      >
        <DialogContent className="max-w-5xl h-[80vh] flex flex-col p-0">
          <DialogHeader className="px-6 py-4 border-b flex flex-row items-center justify-between space-y-0">
            <DialogTitle className="truncate flex-1">{hook.previewFile?.name}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden bg-gray-100">
            <iframe
              src={hook.previewFile?.url}
              className="w-full h-full border-0"
              title={hook.previewFile?.name}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Approval Dialog */}
      <DocumentApprovalDialog
        isOpen={hook.approvalDialog.isOpen}
        onClose={hook.handleCloseApprovalDialog}
        fieldLabel={hook.approvalDialog.fieldLabel}
        onConfirm={hook.handleConfirmApproval}
        isSubmitting={hook.updateReviewMutation.isPending}
      />

      {/* Document Rejection Dialog */}
      <DocumentRejectionDialog
        isOpen={hook.rejectionDialog.isOpen}
        onClose={hook.handleCloseRejectionDialog}
        fieldLabel={hook.rejectionDialog.fieldLabel}
        onConfirm={() => hook.handleConfirmRejection()}
        isSubmitting={hook.updateReviewMutation.isPending}
        initialReason={hook.rejectionDialog.rejectionReason}
        onReasonChange={hook.handleRejectionReasonChange}
      />

      {/* Resend Document Request Dialog */}
      <ResendDocumentRequestDialog
        open={hook.resendDialogOpen}
        onClose={hook.handleCloseResendDialog}
        proposalId={proposalId}
        accountId={account?.id}
        contacts={contacts}
        recipients={recipients}
        creditAnalysisId={creditAnalysis?.id || ''}
        onSuccess={hook.handleResendSuccess}
      />
    </>
  );
}
